import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import {
  type CatalogKeyParts,
  renderCatalogKey,
} from "@p0security/integrations-databricks-shared/catalog-key";
import {
  DATABRICKS_CONNECTOR_FUNCTION_NAME,
  DATABRICKS_TOKEN_AUDIENCE,
} from "@p0security/integrations-databricks-shared/constants";
import type {
  DatabricksComponents,
  DatabricksIntegration,
} from "@p0security/integrations-databricks-shared/types";
import type { DevEnv } from "@p0security/shared/types/environment";
import objectHash from "object-hash";

type Component = keyof DatabricksComponents;

type Item<K extends Component> = DatabricksIntegration[K][string];

/**
 * The connector image the terraform copies into the customer's ECR, named as
 * the Terraform provider's `p0_databricks_connector` example names it. Its tag
 * is the `sha-<commit>` tag that p0-security-perimeter's docker workflow
 * pushes for each merge to main, and its digest is the one that tag resolves
 * to (`docker buildx imagetools inspect <name>:<tag>`). Move them together,
 * and with the provider example. Bumping them here changes only the Terraform
 * shown for new installs. An installed connector moves to a new image when
 * its customer changes both in their own copy, as the comment on the rendered
 * locals tells them to.
 */
const CONNECTOR_IMAGE = {
  digest:
    "sha256:1b3b8c31e2b9b542935631847caf956e866342b63d889671b2fae0432a8bd729",
  imageName: `p0security/${DATABRICKS_CONNECTOR_FUNCTION_NAME}`,
  tag: "sha-e7a7816",
} as const;

/**
 * The connector Lambda's timeout, in seconds, the same as P0's other connector
 * Lambdas (terraform-aws-p0-connector's and the RDS security perimeter's).
 * Lambda's default of 3 seconds is too short for a call that exchanges an AWS
 * identity token with Databricks and then calls Databricks with it.
 */
const CONNECTOR_TIMEOUT_SECONDS = 30;

/**
 * How long the connector's logs are kept, in days, the same as P0's GitHub
 * Repositories connector and the Terraform provider's connector example.
 */
const CONNECTOR_LOG_RETENTION_DAYS = 30;

/** Tags the connector's resources, as P0's other connectors tag theirs. */
const P0_TAGS = `{ ManagedBy = "Terraform", ManagedFor = "P0" }`;

/**
 * A terraform string literal holding `value`. JSON escapes quotes, backslashes
 * and control characters the way HCL does, and `$${` and `%%{` stop a value
 * from opening an interpolation or directive.
 *
 * A missing value renders as an empty string, so that an item saved without a
 * field its Terraform needs still renders, rather than break the page that
 * shows it.
 */
export const hclString = (value: string | undefined) =>
  // Replacer functions, because `$$` in a replacement string means one `$`.
  JSON.stringify(value ?? "")
    .replace(/\$\{/g, () => "$${")
    .replace(/%\{/g, () => "%%{");

/**
 * The name of every resource, data source and provider alias one item's
 * terraform declares. It is unique per item, so the blocks for several items
 * can share a configuration, and another item's blocks can refer to them.
 */
export const tfName = (component: Component, id: string) =>
  `p0_databricks_${component}_${objectHash({ id }).slice(0, 8)}`;

/** The output that holds an account's service principal application ID. */
export const applicationIdOutput = (accountId: string) =>
  `${tfName("account", accountId)}_application_id`;

/**
 * The Terraform and provider versions every item's terraform needs. aws 6.26.0
 * added the outbound identity federation resource, but this asks for 6.36.0,
 * which added its data source, to match the one version that P0's docs and
 * Terraform provider examples give. databricks 1.113.0 made `databricks_grant`
 * honor `provider_config` with an account-level provider, and 1.115.0 fixed the
 * account-level reads that broke after it. docker reads the digest the
 * connector image's tag resolves to, as P0's Docker Hub connector does.
 *
 * The Terraform version is in a block of its own, so that a configuration that
 * already has a `required_providers` block can take this one as it is.
 */
export const PROVIDERS_TERRAFORM = `terraform {
  # 1.4 added terraform_data.
  required_version = ">= 1.4"
}

terraform {
  required_providers {
    aws = {
      source  = "hashicorp/aws"
      version = ">= 6.36.0"
    }
    databricks = {
      source  = "databricks/databricks"
      version = ">= 1.115.0"
    }
    docker = {
      source  = "kreuzwerker/docker"
      version = "~> 3.0"
    }
  }
}`;

/**
 * The connector in AWS account `awsAccountId`: its Lambda, outside any VPC,
 * the Lambda's role and log group, P0's role's permission to invoke it, and
 * the account's outbound identity federation, which lets the Lambda sign in to
 * Databricks without a secret.
 */
export const connectorTerraform = (
  context: DevEnv,
  awsAccountId: string,
  { domainPattern, region }: Pick<Item<"connector">, "domainPattern" | "region">
) => {
  const name = tfName("connector", awsAccountId);
  const provider = `aws.${name}`;
  const { digest, imageName, tag } = CONNECTOR_IMAGE;
  const repositoryUrl = `aws_ecr_repository.${name}.repository_url`;
  const sourceImage = `local.${name}_image_name`;
  const imageTag = `local.${name}_image_tag`;
  const imageDigest = `local.${name}_image_digest`;

  return `locals {
  # The connector image's version: the sha- tag P0 publishes each release
  # under, and the digest that tag must resolve to. Docker Hub lets a tag be
  # pushed again, so the plan fails if the tag no longer resolves to this
  # digest. To update, take both from the latest p0_databricks_connector
  # example in P0's Terraform registry docs: they change together. Changing
  # them copies the image again and redeploys the Lambda.
  ${name}_image_name   = "${imageName}"
  ${name}_image_tag    = "${tag}"
  ${name}_image_digest = "${digest}"
}

# P0 invokes the connector as the ${DATABRICKS_CONNECTOR_FUNCTION_NAME} Lambda
# in this account and region, so deploy it exactly there. Add the credential
# settings your other AWS providers use, such as profile or assume_role, if
# they have any.
provider "aws" {
  alias               = "${name}"
  region              = ${hclString(region)}
  allowed_account_ids = [${hclString(awsAccountId)}]
}

# Lets the connector mint AWS identity tokens, which Databricks accepts in place
# of a secret. This is a setting of the whole AWS account: destroying this
# resource turns outbound identity federation off for everything in it. If it
# is already on, import it rather than create it. To check, run this with AWS
# CLI 2.32.0 or later:
#   aws iam get-outbound-web-identity-federation-info
# If it's on, the output shows "JwtVendingEnabled": true and the issuer URL as
# IssuerIdentifier. If it's off, the command fails with FeatureDisabled. Import
# it with:
#   terraform import aws_iam_outbound_web_identity_federation.${name} ${awsAccountId}
# To remove the connector but leave federation on, replace this block with a
# removed block (Terraform 1.7+) before you destroy the rest. Terraform then
# forgets the setting without turning it off:
#   removed {
#     from = aws_iam_outbound_web_identity_federation.${name}
#     lifecycle {
#       destroy = false
#     }
#   }
resource "aws_iam_outbound_web_identity_federation" "${name}" {
  provider = ${provider}
}

resource "aws_iam_role" "${name}" {
  provider = ${provider}
  name     = "${DATABRICKS_CONNECTOR_FUNCTION_NAME}"
  tags     = ${P0_TAGS}
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Principal = { Service = "lambda.amazonaws.com" }
      Action    = "sts:AssumeRole"
    }]
  })
}

# The log group Lambda writes the connector's logs to.
resource "aws_cloudwatch_log_group" "${name}" {
  provider          = ${provider}
  name              = "/aws/lambda/${DATABRICKS_CONNECTOR_FUNCTION_NAME}"
  retention_in_days = ${CONNECTOR_LOG_RETENTION_DAYS}
  tags              = ${P0_TAGS}
}

# The connector writes its own logs, and no others.
resource "aws_iam_role_policy" "${name}_logs" {
  provider = ${provider}
  name     = "${DATABRICKS_CONNECTOR_FUNCTION_NAME}-logs"
  role     = aws_iam_role.${name}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = ["logs:CreateLogStream", "logs:PutLogEvents"]
      Resource = "\${aws_cloudwatch_log_group.${name}.arn}:*"
    }]
  })
}

# Only tokens for Databricks. Every Databricks federation policy that trusts
# this role expects this audience.
resource "aws_iam_role_policy" "${name}" {
  provider = ${provider}
  name     = "${DATABRICKS_CONNECTOR_FUNCTION_NAME}-identity-token"
  role     = aws_iam_role.${name}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "sts:GetWebIdentityToken"
      Resource = "*"
      Condition = {
        "ForAllValues:StringEquals" = { "sts:IdentityTokenAudience" = ["${DATABRICKS_TOKEN_AUDIENCE}"] }
      }
    }]
  })
}

data "docker_registry_image" "${name}" {
  name = "\${${sourceImage}}:\${${imageTag}}"

  lifecycle {
    postcondition {
      condition     = self.sha256_digest == ${imageDigest}
      error_message = "The connector image's tag doesn't resolve to the digest pinned here. Take both from the latest p0_databricks_connector example in P0's Terraform registry docs, or contact support@p0.dev."
    }
  }
}

# Lambda only runs images from Amazon ECR, so this copies P0's image into a
# repository in this account. It needs docker and the AWS CLI, signed in to this
# account, wherever Terraform runs.
resource "aws_ecr_repository" "${name}" {
  provider = ${provider}
  name     = "${DATABRICKS_CONNECTOR_FUNCTION_NAME}"
  # Tags can't be overwritten, so no one who can push to this repository can
  # replace the image that signs in to Databricks as P0's service principal.
  image_tag_mutability = "IMMUTABLE"
  force_delete         = true
  tags                 = ${P0_TAGS}

  image_scanning_configuration {
    scan_on_push = true
  }
}

# Copies the pinned image again whenever its tag or digest changes, which rolls
# out a new connector release, tagged with that tag and this copy's id. ECR's
# tags can't be overwritten, so each copy needs a tag of its own: copying the
# image again, to roll back or after an interrupted apply, pushes a new one.
resource "terraform_data" "${name}" {
  triggers_replace = {
    digest         = data.docker_registry_image.${name}.sha256_digest
    repository_url = ${repositoryUrl}
    tag            = ${imageTag}
  }

  provisioner "local-exec" {
    # Signs Docker out of ECR at the end, so its login doesn't outlast the copy.
    # A failed sign-out doesn't fail the copy once the image is pushed.
    command = <<-EOT
      set -e
      trap 'docker logout "$REGISTRY" || true' EXIT
      aws ecr get-login-password --region "$REGION" |
        docker login --username AWS --password-stdin "$REGISTRY"
      docker pull --platform linux/amd64 "$SOURCE"
      docker tag "$SOURCE" "$TARGET"
      docker push "$TARGET"
    EOT

    environment = {
      REGION   = ${hclString(region)}
      REGISTRY = split("/", ${repositoryUrl})[0]
      SOURCE   = "\${${sourceImage}}@\${${imageDigest}}"
      TARGET   = "\${${repositoryUrl}}:\${${imageTag}}-\${self.id}"
    }
  }
}

# Pulling one platform and pushing it can give ECR a different manifest from
# the one the tag names on Docker Hub, so the digest there can differ from the
# one pinned above. Lambda needs ECR's.
data "aws_ecr_image" "${name}" {
  provider        = ${provider}
  repository_name = aws_ecr_repository.${name}.name
  image_tag       = "\${${imageTag}}-\${terraform_data.${name}.id}"
}

resource "aws_lambda_function" "${name}" {
  provider      = ${provider}
  function_name = "${DATABRICKS_CONNECTOR_FUNCTION_NAME}"
  role          = aws_iam_role.${name}.arn
  package_type  = "Image"
  image_uri     = "\${${repositoryUrl}}@\${data.aws_ecr_image.${name}.image_digest}"
  architectures = ["x86_64"]
  timeout       = ${CONNECTOR_TIMEOUT_SECONDS}
  tags          = ${P0_TAGS}

  environment {
    variables = {
      # The connector refuses any user whose whole email domain doesn't match.
      DOMAIN_PATTERN = ${hclString(domainPattern)}
    }
  }

  # The connector's role can't create a log group, so its own must exist
  # before it runs.
  depends_on = [aws_cloudwatch_log_group.${name}]
}

# Lets P0 invoke the connector, through the role P0's AWS integration assumes in
# this account.
resource "aws_iam_role_policy" "${name}_p0_invoke_connector" {
  provider = ${provider}
  name     = "${DATABRICKS_CONNECTOR_FUNCTION_NAME}-invoke"
  role     = "${awsRoleName(context, "iam-write")}"
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "lambda:InvokeFunction"
      Resource = aws_lambda_function.${name}.arn
    }]
  })
}`;
};

/**
 * P0's service principal in the Databricks account `accountId`, through an
 * account-level provider: the principal, and the federation policy that lets
 * the connector's Lambda role sign in as it. It holds no admin role: see
 * {@link accountAdminTerraform}.
 */
export const accountTerraform = (
  accountId: string,
  { accountsUrl, connector }: Pick<Item<"account">, "accountsUrl" | "connector">
) => {
  const name = tfName("account", accountId);
  const connectorName = tfName("connector", connector);

  return `provider "databricks" {
  alias      = "${name}"
  host       = ${hclString(accountsUrl)}
  account_id = ${hclString(accountId)}
}

resource "databricks_service_principal" "${name}" {
  provider     = databricks.${name}
  display_name = "p0-connector"
}

# Lets the connector's Lambda role sign in as this principal with an AWS
# identity token, so no secret is ever issued.
resource "databricks_service_principal_federation_policy" "${name}" {
  provider             = databricks.${name}
  service_principal_id = databricks_service_principal.${name}.id
  oidc_policy = {
    issuer    = aws_iam_outbound_web_identity_federation.${connectorName}.issuer_identifier
    subject   = aws_iam_role.${connectorName}.arn
    audiences = ["${DATABRICKS_TOKEN_AUDIENCE}"]
  }
}

output "${applicationIdOutput(accountId)}" {
  description = "Enter this in P0 as the service principal application ID"
  value       = databricks_service_principal.${name}.application_id
}`;
};

/**
 * Gives the service principal of the account `accountId` account admin, which
 * granting account-admin requests takes. Only an account with account-admin
 * requests turned on needs it.
 */
export const accountAdminTerraform = (accountId: string) => {
  const name = tfName("account", accountId);

  return `resource "databricks_service_principal_role" "${name}" {
  provider             = databricks.${name}
  service_principal_id = databricks_service_principal.${name}.id
  role                 = "account_admin"
}`;
};

/**
 * Assigns the service principal of the workspace's account to the workspace:
 * as a workspace admin when `admin`, which granting workspace-admin requests
 * takes, and as a user otherwise, which is all granting privileges on its
 * catalogs takes.
 */
export const workspaceTerraform = (
  workspaceId: string,
  { account }: Pick<Item<"workspace">, "account">,
  { admin }: { admin: boolean }
) => {
  const accountName = tfName("account", account);

  return `resource "databricks_mws_permission_assignment" "${tfName("workspace", workspaceId)}" {
  provider     = databricks.${accountName}
  workspace_id = ${hclString(workspaceId)}
  principal_id = databricks_service_principal.${accountName}.id
  permissions  = ["${admin ? "ADMIN" : "USER"}"]
}`;
};

/**
 * Grants the service principal of the workspace's account MANAGE on the
 * catalog, through the workspace.
 *
 * @param accountId The account of the catalog's workspace.
 */
export const catalogTerraform = ({
  accountId,
  catalog,
  workspaceId,
}: CatalogKeyParts & { accountId: string }) => {
  const accountName = tfName("account", accountId);
  const name = tfName("catalog", renderCatalogKey({ catalog, workspaceId }));

  return `# databricks_grant manages only this principal's grants on the catalog. Never
# use databricks_grants here: it overwrites every grant on the catalog.
resource "databricks_grant" "${name}" {
  provider   = databricks.${accountName}
  catalog    = ${hclString(catalog)}
  principal  = databricks_service_principal.${accountName}.application_id
  privileges = ["MANAGE"]

  provider_config {
    workspace_id = ${hclString(workspaceId)}
  }
}`;
};
